import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useSocket } from '../hooks/useSocket';
import type { AppNotification } from '../types';
import { playNotificationSound } from '../utils/sound';
import { closeBrowserNotifications, notificationTag, showBrowserNotification } from '../utils/browserNotify';

export default function NotificationsBell() {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [open, setOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const socket = useSocket('notifications');
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  function load() {
    apiClient
      .get<AppNotification[]>('/notifications')
      .then(({ data }) => {
        // Прочитанные не показываем: просмотрел — уведомление ушло, как в телефоне
        setNotifications(data.filter((n) => !n.readAt));
        setLoadError(false);
      })
      .catch((err) => {
        console.error('Не удалось загрузить уведомления', err);
        setLoadError(true);
      });
  }

  useEffect(load, []);

  useEffect(() => {
    if (!socket) return;
    const onNotification = (n: AppNotification) => {
      setNotifications((prev) => [n, ...prev]);
      // Сообщения чата (личного и общих каналов) уже озвучивает и показывает Layout.tsx —
      // он слушает сырой сокет чата напрямую и покрывает все случаи (не только те, на кого
      // формально заведено уведомление). Если играть звук/показывать тост ещё и здесь —
      // получится два уведомления на одно сообщение
      if (n.type !== 'CHAT_MESSAGE') {
        playNotificationSound();
        showBrowserNotification(n.title, n.body, notificationTag(n));
      }
    };
    // Прочитано в другом месте (открыл наряд, прочитал чат, другая вкладка) — убираем и здесь,
    // и всплывающие уведомления Windows по той же метке
    const onRead = (r: { tag?: string; ids?: string[]; all?: boolean }) => {
      if (r.all) {
        setNotifications([]);
        closeBrowserNotifications();
        return;
      }
      if (r.ids) {
        const ids = new Set(r.ids);
        setNotifications((prev) => prev.filter((n) => !ids.has(n.id)));
      }
      if (r.tag) {
        setNotifications((prev) => prev.filter((n) => notificationTag(n) !== r.tag));
        closeBrowserNotifications(r.tag);
      }
    };
    socket.on('notification', onNotification);
    socket.on('notifications:read', onRead);
    return () => {
      socket.off('notification', onNotification);
      socket.off('notifications:read', onRead);
    };
  }, [socket]);

  // Раньше отметка "непрочитано" гасла в тот же миг, как диспетчер открывал список — он
  // ещё не успевал прочитать ни одного сообщения. Теперь список остаётся подсвеченным, пока
  // открыт, а прочитанным всё помечается только когда диспетчер его закрывает (успел увидеть)
  // Гасим всё одним запросом. Раньше здесь был запрос на каждое непрочитанное — при
  // накопившемся хвосте закрытие колокольчика давало залп в сотни запросов, лимитер
  // (100 в минуту на IP) отбивал лишние, и весь офис за одним адресом получал
  // «Не удалось…» на любой запрос в ту же минуту.
  // Ошибку больше не глушим молча: если сервер отказал, возвращаем отметки как были —
  // иначе на экране «прочитано», а в базе нет, и при следующем входе всё всплывёт заново
  function markSeenAsRead() {
    setNotifications((prev) => {
      const unreadIds = new Set(prev.filter((n) => !n.readAt).map((n) => n.id));
      if (unreadIds.size === 0) return prev;
      apiClient.patch('/notifications/read-all').catch((err) => {
        console.error('Не удалось отметить уведомления прочитанными', err);
        // Откатываем только те, что сами пометили, — пришедшие за это время не трогаем
        load();
      });
      // Просмотренные уходят из списка — в колокольчике остаётся только новое
      return [];
    });
  }

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen((wasOpen) => {
          if (wasOpen) markSeenAsRead();
          return false;
        });
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleOpen() {
    setOpen((wasOpen) => {
      if (wasOpen) markSeenAsRead();
      return !wasOpen;
    });
  }

  function handleNotificationClick(n: AppNotification) {
    if (!n.orderId) return;
    markSeenAsRead();
    setOpen(false);
    navigate(`/orders/${n.orderId}`);
  }

  const unreadCount = notifications.filter((n) => !n.readAt).length;

  return (
    <div className="notifications-bell" ref={containerRef}>
      <button className="text-icon-btn bell-button" onClick={handleOpen} aria-label="Уведомления">
        Уведомления
        {unreadCount > 0 && <span className="badge bell-badge">{unreadCount}</span>}
      </button>
      {open && (
        <div className="notifications-dropdown">
          {loadError && <p className="error-text">Не удалось загрузить уведомления</p>}
          {!loadError && notifications.length === 0 && <p className="muted">Новых уведомлений нет</p>}
          {notifications.map((n) => (
            <div
              key={n.id}
              className={`notification-item ${!n.readAt ? 'unread' : ''} ${n.orderId ? 'clickable' : ''}`}
              onClick={n.orderId ? () => handleNotificationClick(n) : undefined}
              role={n.orderId ? 'button' : undefined}
            >
              <div className="notification-title">{n.title}</div>
              <div className="notification-body">{n.body}</div>
              <div className="notification-time">{new Date(n.createdAt).toLocaleString('ru-RU')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
